import { create } from 'zustand';
import { defaultTheme } from '@/theme/theme';
import type { ThemeConfig, SemanticColors } from '@/theme/theme.types';
import type { BrandingConfig } from '@supernova/shared';

interface ThemeStore {
  branding: BrandingConfig;
  setBranding: (config: BrandingConfig) => void;
  theme: ThemeConfig;
  setThemeColors: (colors: Partial<SemanticColors>) => void;
  setFontFamily: (fontFamily: string) => void;
  resetTheme: () => void;
}

function applyThemeToDOM(theme: ThemeConfig) {
  const root = document.documentElement;
  Object.entries(theme.colors).forEach(([key, value]) => {
    const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
    root.style.setProperty(`--color-${cssKey}`, value);
  });
  root.style.setProperty('--font-sans', theme.typography.fontFamily.sans);
}

export const useThemeStore = create<ThemeStore>((set, get) => ({
  branding: {
    appName: 'Q PaymentZ',
    logoUrl: null,
    faviconUrl: null,
    primaryColor: '#00838d',
    domain: null,
    businessTitle: null,
    businessDescription: null,
  },

  setBranding: (config) => {
    set({ branding: config });
    document.title = config.businessTitle || 'Fintech - Q PaymentZ';

    // Update favicon dynamically
    if (config.faviconUrl) {
      // Remove all existing favicon links to avoid conflicts
      document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']").forEach((el) => el.remove());
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      // Cache-bust to force browser to load the new favicon
      link.href = `${config.faviconUrl}?v=${Date.now()}`;
      document.head.appendChild(link);
    }

    const currentTheme = get().theme;
    const updatedTheme = {
      ...currentTheme,
      colors: {
        ...currentTheme.colors,
        primary: config.primaryColor,
        ring: config.primaryColor,
      },
    };
    set({ theme: updatedTheme });
    document.documentElement.style.setProperty('--brand-primary', config.primaryColor);
    applyThemeToDOM(updatedTheme);
  },

  theme: defaultTheme,

  setThemeColors: (colorOverrides) => {
    const currentTheme = get().theme;
    const updatedTheme = {
      ...currentTheme,
      colors: { ...currentTheme.colors, ...colorOverrides },
    };
    set({ theme: updatedTheme });
    applyThemeToDOM(updatedTheme);
  },

  setFontFamily: (fontFamily) => {
    const currentTheme = get().theme;
    const updatedTheme = {
      ...currentTheme,
      typography: {
        ...currentTheme.typography,
        fontFamily: { ...currentTheme.typography.fontFamily, sans: fontFamily },
      },
    };
    set({ theme: updatedTheme });
    applyThemeToDOM(updatedTheme);
  },

  resetTheme: () => {
    set({ theme: defaultTheme });
    applyThemeToDOM(defaultTheme);
  },
}));
